import { HTMLProps } from 'react';
import { Form, FormControlProps } from 'react-bootstrap';

export type SelectOption<TValue> = {
  group?: string;
  label: string;
  value: TValue;
  optionTestId?: string;
};

type Props<TValue> = {
  value: TValue | undefined | '';
  options?: Array<SelectOption<TValue | undefined>>;
  optionTestId?: string;
  emptyOption?: SelectOption<TValue | undefined>;
  onChange?: (newValue: TValue | undefined) => void;
} & Omit<FormControlProps, 'value' | 'onChange'> &
  Omit<HTMLProps<HTMLSelectElement>, 'value' | 'onChange' | 'size'>;

function SelectOptions<TValue>({ options }: { options: Array<SelectOption<TValue>> }) {
  return (
    <>
      {options.map(option => (
        <option data-testid={option.optionTestId || 'select-option'} key={option.label} value={option.label}>
          {option.label}
        </option>
      ))}
    </>
  );
}
const EMPTY_OPTION = { label: 'No Selection', value: undefined };
export function Select<TValue>({ value, onChange, options, emptyOption, optionTestId, ...rest }: Props<TValue>) {
  const actualEmptyOption = emptyOption || EMPTY_OPTION;
  const selectedOption = options?.find(o => o.value === value);
  const allOptions: Array<SelectOption<TValue | undefined>> =
    emptyOption || !selectedOption ? [actualEmptyOption, ...(options || [])] : options || [];
  const groups = allOptions.reduce<Record<string, Array<SelectOption<TValue | undefined>>>>((acc, o) => {
    return { ...acc, [o.group || '']: [...(acc[o.group || ''] || []), { ...o, optionTestId }] };
  }, {});

  return (
    <Form.Control
      as="select"
      value={selectedOption?.label || actualEmptyOption.label}
      onChange={e => onChange?.(allOptions.find(o => o.label === e.target.value)?.value)}
      {...rest}
    >
      {Object.entries(groups).map(([groupLabel, groupOptions]) =>
        groupLabel ? (
          <optgroup key={groupLabel} label={groupLabel}>
            <SelectOptions options={groupOptions} />
          </optgroup>
        ) : (
          <SelectOptions key="__NO_GROUP__" options={groupOptions} />
        )
      )}
    </Form.Control>
  );
}
